import { HexColor } from '@repo/shared/types';
import { groq } from 'next-sanity';
import { z } from 'zod';

import { link, SanityLink } from './link';
import { media, SanityMedia } from './media';

export const SanityBentoBoxItemMedia = z.object({
  _type: z.literal('bentoBoxItemMedia'),
  _key: z.string(),
  media: SanityMedia,
  hasMediaPadding: z.boolean(),
});
export type SanityBentoBoxItemMedia = z.infer<typeof SanityBentoBoxItemMedia>;

export const SanityBentoBoxItemStat = z.object({
  _type: z.literal('bentoBoxItemStat'),
  _key: z.string(),
  value: z.string(),
  caption: z.string(),
});
export type SanityBentoBoxItemStat = z.infer<typeof SanityBentoBoxItemStat>;

export const SanityBentoBoxItemCta = z.object({
  _type: z.literal('bentoBoxItemCta'),
  _key: z.string(),
  media: SanityMedia,
  headline: z.string(),
  cta: SanityLink,
});
export type SanityBentoBoxItemCta = z.infer<typeof SanityBentoBoxItemCta>;

export const SanityBentoBoxItemContent = z.object({
  _type: z.literal('bentoBoxItemContent'),
  _key: z.string(),
  media: SanityMedia.optional(),
  headline: z.string(),
  body: z.string(),
});
export type SanityBentoBoxItemContent = z.infer<
  typeof SanityBentoBoxItemContent
>;

export const SanityBentoBoxItemReview = z.object({
  _type: z.literal('bentoBoxItemReview'),
  _key: z.string(),
  headline: z.string(),
  bodyCopy: z.string().optional().nullable(),
  name: z.string(),
  url: z.string().optional().nullable(),
  hasReviewStars: z.boolean().optional().nullable(),
});
export type SanityBentoBoxItemReview = z.infer<typeof SanityBentoBoxItemReview>;

export const SanityBentoBoxItem = z.discriminatedUnion('_type', [
  SanityBentoBoxItemMedia,
  SanityBentoBoxItemStat,
  SanityBentoBoxItemCta,
  SanityBentoBoxItemContent,
  SanityBentoBoxItemReview,
]);
export type SanityBentoBoxItem = z.infer<typeof SanityBentoBoxItem>;

export const SanityBentoBox = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('bentoBox'),
  _key: z.string(),
  headline: z.string(),
  boxes: z.array(
    z.object({
      background: HexColor,
      content: SanityBentoBoxItem,
    }),
  ),
});
export type SanityBentoBox = z.infer<typeof SanityBentoBox>;

export const bentoBoxItem = groq`{
  'background': background->color.hex,
  content[0] {
    _type,
    _key,
    _type == 'bentoBoxItemMedia' => {
      media[0] ${media},
      hasMediaPadding,
    },
    _type == 'bentoBoxItemStat' => {
      value,
      caption,
    },
    _type == 'bentoBoxItemCta' => {
      media[0] ${media},
      headline,
      cta[0] ${link},
    },
    _type == 'bentoBoxItemContent' => {
      media[0] ${media},
      headline,
      body,
    },
    _type == 'bentoBoxItemReview' => {
      headline,
      bodyCopy,
      name,
      url,
      hasReviewStars,
    },
  },
}`;
